// src/components/creditnote/CreditNoteReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { decryptData } from "../../utils/encrypt";
import {
  CREDIT_NOTE_SEARCH_API,
  CREDIT_NOTE_DELETE_API,
} from "../../config/api";

const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtDMY = (v) => {
  if (!v) return "-";
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split("-").map(n => parseInt(n, 10));
  if (!y || !m || !d) return v;
  const yy = String(y).slice(-2);
  return `${String(d).padStart(2, "0")}-${MON[m - 1]}-${yy}`;
};
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const defaultDates = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: toYMD(from), to: toYMD(to) };
};
const n3 = (x) => Number(x ?? 0).toFixed(3);

export default function CreditNoteReport() {
  // auth / org
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  // filters
  const { from, to } = defaultDates();
  const [filters, setFilters] = useState({
    credit_note_no: "",
    tally_invoice_no: "",
    reason: "",
    date_from: from,
    date_to: to,
    min_total: "",
    max_total: "",
  });

  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // sorting
  const [sort, setSort] = useState({ by: "credit_date", dir: "desc" });
  const numeric = new Set([
    "invoice_qty_total",
    "credited_qty_total",
    "base_amount",
    "gst_amount",
    "total_amount",
  ]);
  const sIcon = (col) => (sort.by === col ? (sort.dir === "asc" ? "▲" : "▼") : "");
  const onSort = (col) =>
    setSort((s) =>
      s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "asc" }
    );

  // delete
  const [deletingId, setDeletingId] = useState(null);

  // init user/org
  useEffect(() => {
    try {
      const su = localStorage.getItem("suyog_user");
      if (su) {
        const u = decryptData(su);
        setUserId(u?.user_id || null);
      }
    } catch {}
    try {
      const so = localStorage.getItem("suyog_selected_org");
      if (so) {
        const o = JSON.parse(so);
        setOrgId(o?.value || null);
      }
    } catch {}
  }, []);

  // fetch list
  const fetchRows = async (override) => {
    if (!userId || !orgId) return;
    setLoading(true);
    setError("");
    try {
      const f = override ?? filters;
      const payload = {
        user_id: userId,
        org_id: orgId,
        date_from: f.date_from,
        date_to: f.date_to,
        credit_note_no: f.credit_note_no || undefined,
        tally_invoice_no: f.tally_invoice_no || undefined,
        reason: f.reason || undefined,
        min_total: f.min_total || undefined,
        max_total: f.max_total || undefined,
      };
      const res = await fetch(CREDIT_NOTE_SEARCH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json?.success) throw new Error(json?.error || "Failed to load");
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      setRows([]);
      setError(e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && orgId) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orgId]);

  // inputs
  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };
  const onSearch = (e) => {
    e?.preventDefault?.();
    fetchRows();
  };
  const onReset = () => {
    const d = defaultDates();
    const reset = {
      credit_note_no: "",
      tally_invoice_no: "",
      reason: "",
      date_from: d.from,
      date_to: d.to,
      min_total: "",
      max_total: "",
    };
    setFilters(reset);
    fetchRows(reset);
  };

  // sorted rows
  const sortedRows = useMemo(() => {
    const data = rows.slice();
    const { by, dir } = sort;
    data.sort((a, b) => {
      const va = a?.[by];
      const vb = b?.[by];
      if (numeric.has(by)) {
        const na = Number(va ?? 0);
        const nb = Number(vb ?? 0);
        return dir === "asc" ? na - nb : nb - na;
      }
      const sa = (va ?? "").toString();
      const sb = (vb ?? "").toString();
      const cmp = sa.localeCompare(sb);
      return dir === "asc" ? cmp : -cmp;
    });
    return data;
  }, [rows, sort]);

  // totals (dedupe invoiced qty by invoice number)
  const totals = useMemo(() => {
    let invQty = 0, crQty = 0;
    const seen = new Set();
    for (const r of rows) {
      crQty += Number(r.credited_qty_total || 0);

      // gather invoice numbers from any supported field
      const list = extractInvoiceList(r);
      for (const it of list) {
        const key = it.invoice_no?.toString() || "";
        if (key && !seen.has(key)) {
          invQty += Number(it.invoice_qty ?? r.invoice_qty_total ?? 0);
          seen.add(key);
        }
      }
      // fallback if no list present
      if (list.length === 0) {
        const invNo = String(r.tally_invoice_no || "");
        if (invNo && !seen.has(invNo)) {
          invQty += Number(r.invoice_qty_total || 0);
          seen.add(invNo);
        }
      }
    }
    return { invQty, crQty };
  }, [rows]);

  // ----- helpers to show invoices inline -----
  function extractInvoiceList(r) {
    // preferred: r.invoices: [{ invoice_no, invoiced_qty }]
    if (Array.isArray(r?.invoices) && r.invoices.length) {
      return r.invoices.map(x => ({
        invoice_no: x.invoice_no ?? x.tally_invoice_no ?? x.invoice,
        invoice_qty: x.invoiced_qty ?? x.invoice_qty ?? x.qty ?? null,
      })).filter(x => x.invoice_no);
    }
    // alt: r.invoice_map: [{ tally_invoice_no, invoice_qty }]
    if (Array.isArray(r?.invoice_map) && r.invoice_map.length) {
      return r.invoice_map.map(x => ({
        invoice_no: x.tally_invoice_no ?? x.invoice_no ?? x.invoice,
        invoice_qty: x.invoice_qty ?? x.qty ?? null,
      })).filter(x => x.invoice_no);
    }
    // alt: r.invoice_nos: "INV-1,INV-2"
    if (typeof r?.invoice_nos === "string" && r.invoice_nos.trim()) {
      return r.invoice_nos.split(",").map(s => s.trim()).filter(Boolean).map(no => ({
        invoice_no: no,
        invoice_qty: null,
      }));
    }
    // legacy single field
    if (r?.tally_invoice_no) {
      return [{ invoice_no: r.tally_invoice_no, invoice_qty: r.invoice_qty_total ?? null }];
    }
    return [];
  }

  function renderInvoicesCell(r) {
    const list = extractInvoiceList(r);
    if (!list.length) return "-";
    return (
      <div className="d-flex flex-wrap gap-1">
        {list.map((it, idx) => (
          <span key={idx} className="badge text-bg-light border" title={it.invoice_no}>
            {it.invoice_no}
            {it.invoice_qty != null ? (
              <span className="ms-1 text-muted">({n3(it.invoice_qty)})</span>
            ) : null}
          </span>
        ))}
      </div>
    );
  }

  // delete logic (48h window)
  const canDelete = (r) => {
    if (!r?.created_on) return false;
    const dt = new Date(String(r.created_on).replace(" ", "T"));
    if (Number.isNaN(dt.getTime())) return false;
    return Date.now() - dt.getTime() <= 48 * 3600 * 1000;
  };
  const onDelete = async (row) => {
    if (!canDelete(row)) return;
    const ok = window.confirm(`Delete credit note ${row.credit_note_no}? This cannot be undone.`);
    if (!ok) return;
    try {
      setDeletingId(row.id);
      const res = await fetch(CREDIT_NOTE_DELETE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: row.id, org_id: orgId, user_id: userId }),
      });
      const json = await res.json();
      if (!json?.success) throw new Error(json?.error || "Delete failed");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e?.message || "Server error during delete");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4>📄 Credit Note Report</h4>

        {/* Filters */}
        <form className="card mt-3" onSubmit={onSearch}>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Credit Note No</label>
                <input
                  className="form-control"
                  name="credit_note_no"
                  value={filters.credit_note_no}
                  onChange={onChange}
                  placeholder="e.g. CN/25-26/00015"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Tally Invoice No</label>
                <input
                  className="form-control"
                  name="tally_invoice_no"
                  value={filters.tally_invoice_no}
                  onChange={onChange}
                  placeholder="e.g. T-4846/25-26"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Reason</label>
                <select className="form-select" name="reason" value={filters.reason} onChange={onChange}>
                  <option value="">All</option>
                  <option>Returned Material</option>
                  <option>Rate Difference</option>
                  <option>Short Supply</option>
                  <option>Tax Correction</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Date From</label>
                <input type="date" className="form-control" name="date_from" value={filters.date_from} onChange={onChange} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Date To</label>
                <input type="date" className="form-control" name="date_to" value={filters.date_to} onChange={onChange} />
              </div>
            </div>

            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
              <button className="btn btn-secondary" type="button" onClick={onReset} disabled={loading}>
                Reset
              </button>
            </div>

            {error && <div className="text-danger mt-2">{error}</div>}
          </div>
        </form>

        {/* Results */}
        <div className="table-responsive mt-4">
          <table className="table table-bordered table-hover w-100">
            <thead className="table-light">
              <tr>
                <th onClick={() => onSort("credit_note_no")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Credit Note No {sIcon("credit_note_no")}
                </th>
                <th onClick={() => onSort("credit_date")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Credit Date {sIcon("credit_date")}
                </th>
                <th style={{ whiteSpace: "nowrap" }}>Invoices</th>{/* NEW: inline invoices */}
                <th onClick={() => onSort("customer_name")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Customer {sIcon("customer_name")}
                </th>
                <th>Reason</th>
                <th>Remarks</th>
                <th className="text-end" onClick={() => onSort("invoice_qty_total")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Invoiced Qty {sIcon("invoice_qty_total")}
                </th>
                <th className="text-end" onClick={() => onSort("credited_qty_total")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Credited Qty {sIcon("credited_qty_total")}
                </th>
                <th>Created On</th>
                <th style={{ width: "7rem" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr><td colSpan="10" className="text-center">No data</td></tr>
              ) : (
                sortedRows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.credit_note_no}</td>
                    <td>{fmtDMY(r.credit_date)}</td>
                    <td>{renderInvoicesCell(r)}</td>
                    <td>{r.customer_name || "-"}</td>
                    <td>{r.reason}</td>
                    <td>{r.remarks || "-"}</td>
                    <td className="text-end">{n3(r.invoice_qty_total)}</td>
                    <td className="text-end">{n3(r.credited_qty_total)}</td>
                    <td>{fmtDMY(r.created_on)}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        disabled={!canDelete(r) || deletingId === r.id}
                        onClick={() => onDelete(r)}
                        title={canDelete(r) ? "Delete" : "Locked after 2 days"}
                      >
                        {deletingId === r.id ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="table-light">
              <tr>
                <td colSpan={6} className="text-end fw-bold">Totals:</td>
                <td className="text-end fw-bold">{n3(totals.invQty)}</td>
                <td className="text-end fw-bold">{n3(totals.crQty)}</td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <Footer />
    </>
  );
}
